import { useEffect, useState, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { FiUpload } from "react-icons/fi";
import { GiMagnifyingGlass } from "react-icons/gi";
import CaseDetailsModal from "../../components/user-dashboards/CaseDetailsModal";
import { fetchUserCases } from "../../store/user/userSlice";
import {
  fetchSearchCasesByCaseIdAndCaseOwnerAndUserId,
  resetCombinedState,
} from "../../store/common/searchSlice";
import { AppDispatch, RootState } from "../../store/store";
import { useNavigate, useSearchParams } from "react-router-dom";
import { smilePortalCaseHref } from "../../utils/linkedPortalCase";
import { Helmet } from "react-helmet-async";
import { hasCustomCaseView } from "../../constants/customCaseView";
import { isVelvetBrand, isVelvetLabToken, isUrisBrand } from "../../utils/brandLogo";
import JuDailyCasesChart from "../../components/user-dashboards/JuDailyCasesChart";
import api from "../../utils/api";

const entriesPerPage = 50;
const serviceMap: Record<string, string> = {
  "Crown & Bridge": "crownAndBridge",
  Implant: "Implant",
  "Smile Design / Mock-up": "smileDesign",
  "No-prep Veneers": "veneers",
  "LDP Complete denture": "complete-denture",
  "LDP Partial denture": "partial-denture",
  "RPD Framework": "partial-cast",
  "Surgical Guide": "surgicalGuide",
  "Nightguard / Bite splint": "nightguard-bite-splint",
  "Digital model": "digital-model",
  "Other treatments": "otherTreatments",
  "Upload filled order": "filledOrder",
};

const MyCase = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const userId = useSelector((state: RootState) => state.auth.user?.user_id);
  const userRole = useSelector((state: RootState) => state.auth.user?.role);
  const verifySmile = useSelector(
    (state: RootState) => state.auth.user?.verifySmile,
  );
  const isImpersonating = useSelector(
    (state: RootState) => state.impersonation.isImpersonating,
  );
  const userToken = useSelector((state: RootState) => state.auth.user?.token);
  const userPartner = useSelector(
    (state: RootState) => state.auth.user?.partner,
  );
  const hasSmileAccess =
    !!verifySmile || userRole?.toLowerCase() === "super admin";
  /** JU lab — units column, daily units, etc. */
  const isJuClient = isVelvetLabToken(userToken);
  const isVelvet = isVelvetBrand({ token: userToken });
  const isUris = isUrisBrand({ token: userToken, partner: userPartner });
  const isCustomCaseView = hasCustomCaseView(userId, userToken);
  const {
    cases = [],
    isLoading = false,
    totalCount = 0,
    dailyCases = 0,
  } = useSelector((state: RootState) => state.userCases || {});

  // 🔹 Search slice
  const {
    combined: searchedCases = [],
    loading: { combined: searching },
  } = useSelector((state: RootState) => state.search);

  const [currentPage, setCurrentPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCaseData, setSelectedCaseData] = useState<any | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchVisible, setSearchVisible] = useState(false);
  const [localCases, setLocalCases] = useState<any[]>([]);
  const [showCasesGraph, setShowCasesGraph] = useState(false);
  const [urisTokenSummary, setUrisTokenSummary] = useState<{
    mode: "client" | "side_admin";
    balance: number;
    total: number;
    used: number;
    tokenCount: number;
  } | null>(null);

  // 🔹 Sync local copy with Redux
  useEffect(() => {
    setLocalCases(cases);
  }, [cases]);

  const tableColSpan = 6 + (hasSmileAccess ? 1 : 0);

  // 🔹 Fetch normal user cases (default)
  useEffect(() => {
    if (!userId) return;
    if (searchQuery.trim()) return; // avoid refetch when searching
    dispatch(
      fetchUserCases({
        user_id: userId,
        page: currentPage,
        limit: entriesPerPage,
      })
    );
  }, [dispatch, userId, currentPage, searchQuery]);

  useEffect(() => {
    if (!isUris || (userRole !== "User" && userRole !== "Side Admin")) return;
    api
      .get("/api/uris-tokens/me")
      .then((res) => {
        if (res.data?.data?.applies) {
          const mode =
            res.data.data.mode === "side_admin" ? "side_admin" : "client";
          setUrisTokenSummary({
            mode,
            balance: Number(res.data.data.balance) || 0,
            total: Number(res.data.data.total) || 0,
            used: Number(res.data.data.used) || 0,
            tokenCount:
              Number(res.data.data.tokenCount ?? res.data.data.balance) || 0,
          });
        }
      })
      .catch(() => setUrisTokenSummary(null));
  }, [isUris, userRole]);

  // 🔹 Server-side search
  useEffect(() => {
    if (!userId) return;

    const trimmed = searchQuery.trim();
    if (trimmed.length === 0) {
      dispatch(resetCombinedState());
      return;
    }

    const delay = setTimeout(() => {
      dispatch(
        fetchSearchCasesByCaseIdAndCaseOwnerAndUserId({
          caseOwner: trimmed,
          userId,
          limit: entriesPerPage,
        })
      );
    }, 400); // debounce search

    return () => clearTimeout(delay);
  }, [dispatch, searchQuery, userId]);

  const totalPages = Math.ceil(totalCount / entriesPerPage) || 1;

  // 🔹 Show searched cases if searching
  const displayedCases = useMemo(() => {
    if (searchQuery.trim()) return searchedCases;
    return localCases;
  }, [searchedCases, localCases, searchQuery]);

  // 🔹 Handle modal open
  const handleOpenModal = useCallback(
    (caseId: string) => {
      if (!isImpersonating) {
        setLocalCases((prev) =>
          prev.map((c) =>
            c.case_id === caseId ? { ...c, pending_comments: 0 } : c
          )
        );
      }

      const selected = displayedCases.find((c) => c.case_id === caseId);
      if (selected) {
        setSelectedCaseData(selected);
        setModalOpen(true);
      }
    },
    [displayedCases, isImpersonating]
  );

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setSelectedCaseData(null);
  }, []);

  useEffect(() => {
    const linkedCaseId = searchParams.get("case");
    if (!linkedCaseId || isLoading || searching) return;
    const selected = displayedCases.find((c) => c.case_id === linkedCaseId);
    if (!selected) return;
    handleOpenModal(linkedCaseId);
    const next = new URLSearchParams(searchParams);
    next.delete("case");
    setSearchParams(next, { replace: true });
  }, [
    searchParams,
    displayedCases,
    isLoading,
    searching,
    handleOpenModal,
    setSearchParams,
  ]);

  const formatDate = useCallback((ms?: number) => {
    if (!ms) return "—";
    return new Date(ms).toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }, []);

  const getUserCustomCaseStyle = useCallback(
    (item: { status?: string; has_redesign?: boolean | null }) => {
      if (!isCustomCaseView) return null;

      const statusLower = item.status?.toLowerCase() || "";

      if (statusLower === "completed") {
        // has_redesign true → final; otherwise treat as V1 (covers false/null/undefined)
        if (item.has_redesign === true) {
          return {
            statusLabel: "V-Final-Done",
            rowClass: "bg-[#B9DDB9]",
            statusBadgeClass: "bg-[#80C584]",
            statusTextClass: "text-[#1F5C24]",
            borderClass: "border-[#8FB98F]",
          };
        }
        return {
          statusLabel: "V1-Done",
          rowClass: "bg-[#E7F4EA]",
          statusBadgeClass: "bg-[#C8E6CA]",
          statusTextClass: "text-[#3D7A45]",
          borderClass: "border-[#B8D9C0]",
        };
      }
      if (statusLower === "submitted") {
        return {
          statusLabel: "Under Process",
          rowClass: "bg-[#E3EFFB]",
          statusBadgeClass: "bg-[#CCE4F8]",
          statusTextClass: "text-[#2E6A9E]",
          borderClass: "border-[#B5D4F0]",
        };
      }
      if (statusLower === "redesigning") {
        return {
          statusLabel: "Under Amendment",
          rowClass: "bg-[#FDE9C8]",
          statusBadgeClass: "bg-[#FCE1B4]",
          statusTextClass: "text-[#A66B10]",
          borderClass: "border-[#F0D4A0]",
        };
      }
      if (statusLower === "submitted for milling") {
        return {
          statusLabel: "Submitted for Milling",
          rowClass: "bg-[#E8E8E8]",
          statusBadgeClass: "bg-[#CED8DF]",
          statusTextClass: "text-[#4A5D68]",
          borderClass: "border-[#C8C8C8]",
        };
      }
      if (statusLower.includes("files pending")) {
        return {
          statusLabel: item.status || "Files Pending",
          rowClass: "bg-[#FCE8E7]",
          statusBadgeClass: "bg-[#F8D0D1]",
          statusTextClass: "text-[#B0484A]",
          borderClass: "border-[#E8BEBC]",
        };
      }

      return null;
    },
    [isCustomCaseView]
  );

  const getUrisStatusStyle = useCallback((status?: string) => {
    const statusLower = status?.toLowerCase() || "";
    if (statusLower === "completed") {
      return {
        pill: "bg-[rgba(127,199,154,0.12)] text-[#8fd3a8]",
        dot: "bg-[#8fd3a8]",
      };
    }
    if (statusLower === "submitted" || statusLower.includes("milling")) {
      return {
        pill: "bg-[rgba(229,164,67,0.13)] text-[#EDB766]",
        dot: "bg-[#EDB766]",
      };
    }
    if (
      statusLower.includes("pending") ||
      statusLower.includes("duplicate") ||
      statusLower.includes("hold")
    ) {
      return {
        pill: "bg-[rgba(191,30,46,0.16)] text-[#F3A0A6]",
        dot: "bg-[#F06A74]",
      };
    }
    return {
      pill: "bg-[rgba(156,154,155,0.16)] text-[#d6d4d5]",
      dot: "bg-[#d6d4d5]",
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>My Cases</title>
      </Helmet>
      <div
        className={`${
          isUris
            ? "bg-transparent text-gray-300"
            : isCustomCaseView
            ? "p-0 overflow-hidden rounded-none border-0 shadow-none bg-white"
            : "p-6 border-2 border-[#DDDDD8] rounded-2xl bg-white"
        } text-sm ${isVelvet ? "velvet-cases-scope" : ""}`}
      >
        {isUris ? (
          <div className="uris-scope flex flex-col gap-5">
            {urisTokenSummary ? (
              urisTokenSummary.mode === "side_admin" ? (
                <section
                  className="grid grid-cols-1 gap-4 sm:grid-cols-1 sm:max-w-md"
                  aria-label="Token count"
                >
                  <div className="flex items-center gap-4 rounded-[14px] border border-[rgba(191,30,46,0.6)] bg-[#221c20] px-5 py-[18px]">
                    <span className="flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-xl bg-[rgba(191,30,46,0.2)] text-[#F06A74]">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="8"/><path d="M12 8v8M9 10.5h4.5a1.5 1.5 0 0 1 0 3h-3a1.5 1.5 0 0 0 0 3H15"/></svg>
                    </span>
                    <div className="min-w-0">
                      <div className="text-[12.5px] font-semibold text-[#9C9A9B]">
                        Token count
                      </div>
                      <div className="mt-0.5 font-[Montserrat,sans-serif] text-[28px] font-bold tabular-nums leading-tight text-white">
                        {urisTokenSummary.tokenCount}
                      </div>
                      <div className="mt-1 text-[12px] text-[#9C9A9B]">
                        Unlimited · +1 each time you create a case
                      </div>
                    </div>
                  </div>
                </section>
              ) : (
              <section
                className="grid grid-cols-1 gap-4 sm:grid-cols-3"
                aria-label="Token summary"
              >
                <div className="flex items-center gap-4 rounded-[14px] border border-[#2e2f34] bg-[#1d1e21] px-5 py-[18px]">
                  <span className="flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-xl bg-[#24252a] text-[#bdbbbc]">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 3l8 4.5v9L12 21l-8-4.5v-9z"/><path d="M12 12l8-4.5M12 12v9M12 12L4 7.5"/></svg>
                  </span>
                  <div>
                    <div className="text-[12.5px] font-semibold text-[#9C9A9B]">
                      Total tokens in package
                    </div>
                    <div className="mt-0.5 font-[Montserrat,sans-serif] text-[28px] font-bold tabular-nums leading-tight text-white">
                      {urisTokenSummary.total}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4 rounded-[14px] border border-[#2e2f34] bg-[#1d1e21] px-5 py-[18px]">
                  <span className="flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-xl bg-[#24252a] text-[#bdbbbc]">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="9"/><path d="M8 12.5l2.8 2.8L16.5 9.5"/></svg>
                  </span>
                  <div>
                    <div className="text-[12.5px] font-semibold text-[#9C9A9B]">
                      Tokens used
                    </div>
                    <div className="mt-0.5 font-[Montserrat,sans-serif] text-[28px] font-bold tabular-nums leading-tight text-white">
                      {urisTokenSummary.used}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4 rounded-[14px] border border-[rgba(191,30,46,0.6)] bg-[#221c20] px-5 py-[18px]">
                  <span className="flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-xl bg-[rgba(191,30,46,0.2)] text-[#F06A74]">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="8"/><path d="M12 8v8M9 10.5h4.5a1.5 1.5 0 0 1 0 3h-3a1.5 1.5 0 0 0 0 3H15"/></svg>
                  </span>
                  <div className="min-w-0">
                    <div className="text-[12.5px] font-semibold text-[#9C9A9B]">
                      Balance tokens
                    </div>
                    <div className="mt-0.5 font-[Montserrat,sans-serif] text-[28px] font-bold tabular-nums leading-tight text-white">
                      {urisTokenSummary.balance}
                    </div>
                    {urisTokenSummary.total > 0 ? (
                      <div
                        className="mt-2 h-1.5 w-[180px] max-w-full overflow-hidden rounded-full bg-[#2e2f34]"
                        role="img"
                        aria-label="Balance as share of package"
                      >
                        <i
                          className="block h-full rounded-full bg-[#BF1E2E]"
                          style={{
                            width: `${Math.min(
                              100,
                              Math.round(
                                (urisTokenSummary.balance /
                                  urisTokenSummary.total) *
                                  100,
                              ),
                            )}%`,
                          }}
                        />
                      </div>
                    ) : null}
                  </div>
                </div>
              </section>
              )
            ) : null}

            <div className="flex flex-wrap items-center justify-between gap-4">
              <h1 className="m-0 font-[Montserrat,sans-serif] text-[18px] font-bold text-white">
                My Cases
              </h1>
              <div className="flex flex-wrap items-center gap-3.5">
                {searchQuery ? (
                  <span className="whitespace-nowrap text-[12.5px] text-[#F3A0A6]">
                    Showing at most 100 recent records
                  </span>
                ) : (
                  <span className="whitespace-nowrap text-[12.5px] text-[#9C9A9B]">
                    Showing{" "}
                    <b className="text-[#e9e7e8]">{displayedCases.length}</b> of{" "}
                    <b className="text-[#e9e7e8]">{totalCount}</b> cases
                  </span>
                )}
                <div className="relative">
                  <svg
                    className="pointer-events-none absolute left-[11px] top-1/2 -mt-2 text-[#8a888b]"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="11" cy="11" r="7" />
                    <path d="M20 20l-4-4" />
                  </svg>
                  <input
                    type="search"
                    placeholder="Search patient, case ID, service"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-10 w-[260px] max-w-full rounded-lg border border-[#37383e] bg-[#141517] py-0 pl-[34px] pr-3 text-[13.5px] text-[#f2f0f1] outline-none placeholder:text-[#77757a] focus:border-[#BF1E2E] focus:shadow-[0_0_0_3px_rgba(191,30,46,0.28)]"
                  />
                </div>
                <div className="flex items-center gap-1.5 text-[12.5px] text-[#9C9A9B]">
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                    className="flex h-[34px] w-[34px] items-center justify-center rounded-lg border border-[#37383e] bg-[#141517] text-[#bdbbbc] hover:border-[#5a5b61] hover:text-white disabled:opacity-40"
                    aria-label="Previous page"
                  >
                    ‹
                  </button>
                  <span>
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setCurrentPage((p) => Math.min(p + 1, totalPages))
                    }
                    disabled={currentPage === totalPages}
                    className="flex h-[34px] w-[34px] items-center justify-center rounded-lg border border-[#37383e] bg-[#141517] text-[#bdbbbc] hover:border-[#5a5b61] hover:text-white disabled:opacity-40"
                    aria-label="Next page"
                  >
                    ›
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : isCustomCaseView ? (
          <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-5 bg-white">
            <div className="flex items-baseline gap-3">
              <div className="font-bold text-[#1a1a1a] text-2xl tracking-tight">
                My Cases
              </div>
              {isJuClient && (
                <div className="flex items-center gap-2 text-sm">
                  <div
                    className="flex items-baseline gap-1.5"
                    title="Cases uploaded since 7:00 AM UK time. Resets daily at 7:00 AM UK."
                  >
                    <span className="font-medium text-[#707070]">Today's cases</span>
                    <span className="font-semibold text-[#A65D4A]">{dailyCases}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowCasesGraph(true)}
                    className="px-2.5 py-1 rounded-full text-xs font-semibold bg-[#FCF2E5] text-[#2C2C2C] hover:bg-[#F0E6D4]"
                  >
                    Graph
                  </button>
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center justify-end gap-3 ml-auto">
              {searchQuery ? (
                <span className="text-red-500 text-xs whitespace-nowrap">
                  Showing atmost 100 recent records
                </span>
              ) : (
                <div className="flex items-center gap-1 text-[#707070] text-xs whitespace-nowrap">
                  <span>
                    Showing <b className="text-[#333333]">{displayedCases.length}</b> of{" "}
                    {totalCount} cases
                  </span>
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-1.5 py-0.5 rounded disabled:opacity-40 hover:bg-[#F9F1EB]"
                  >
                    ‹
                  </button>
                  <span className="px-1">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() =>
                      setCurrentPage((p) => Math.min(p + 1, totalPages))
                    }
                    disabled={currentPage === totalPages}
                    className="px-1.5 py-0.5 rounded disabled:opacity-40 hover:bg-[#F9F1EB]"
                  >
                    ›
                  </button>
                </div>
              )}
              <input
                type="text"
                placeholder="Search by Case ID or Owner..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-52 sm:w-60 px-3 py-1.5 text-xs text-[#555555] rounded-lg border border-[#E8E0D8] bg-white focus:outline-none focus:ring-1 focus:ring-[#D4C4A8] focus:border-[#D4C4A8]"
              />
            </div>
          </div>
        ) : (
          <div className="flex justify-between gap-2 mb-4 items-center text-sm text-gray-600 relative">
            <div className="flex items-center gap-3">
              <div className="font-bold text-black text-lg">My Cases</div>
              {isJuClient && (
                <div className="flex items-center gap-2">
                  <div
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                      isVelvet
                        ? "bg-[#FCF2E5] text-[#2C2C2C]"
                        : "bg-[#E8F6F8] text-[#1A7A86]"
                    }`}
                    title="Cases uploaded since 7:00 AM UK time. Resets daily at 7:00 AM UK."
                  >
                    <span className="font-medium text-gray-600">Today's cases</span>
                    <span className={isVelvet ? "text-[#A65D4A]" : undefined}>{dailyCases}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowCasesGraph(true)}
                    className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                      isVelvet
                        ? "bg-[#FCF2E5] text-[#2C2C2C] hover:bg-[#F0E6D4]"
                        : "bg-[#29a3b4] text-white hover:bg-[#208a99]"
                    }`}
                  >
                    Graph
                  </button>
                </div>
              )}
            </div>
            <div
              className={`absolute flex items-center justify-center transition-all duration-300 ease-in-out overflow-hidden ${searchVisible ? "right-64 opacity-100" : "right-0"
                }`}
            >
              {searchQuery ? (
                <div className="text-red-500">
                  Showing atmost 100 recent records
                </div>
              ) : (
                <div>
                  <span>
                    Showing <b>{displayedCases.length}</b> of {totalCount} cases
                  </span>
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-2 py-1 rounded disabled:opacity-40 hover:bg-gray-100"
                  >
                    ‹
                  </button>
                  <span className="px-2">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() =>
                      setCurrentPage((p) => Math.min(p + 1, totalPages))
                    }
                    disabled={currentPage === totalPages}
                    className="px-2 py-1 rounded disabled:opacity-40 hover:bg-gray-100"
                  >
                    ›
                  </button>

                  <button onClick={() => setSearchVisible((v) => !v)}>
                    <GiMagnifyingGlass
                      size={18}
                      className="cursor-pointer scale-x-[-1] transition-transform duration-300"
                    />
                  </button>
                </div>
              )}
            </div>

            <div
              className={`absolute right-0 p-1 transition-all duration-300 ease-in-out overflow-hidden ${searchVisible ? "w-64 opacity-100" : "w-0 opacity-0"
                }`}
            >
              <input
                type="text"
                placeholder="Search by Case ID or Owner..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full px-2 rounded-2xl py-1 text-sm border border-gray-300 focus:outline-none focus:ring-2 ${
                  isVelvet ? "focus:ring-[#FCF2E5]" : "focus:ring-[#29a3b4]"
                }`}
              />
            </div>
          </div>
        )}

        <div
          className={
            isUris
              ? "mt-5 overflow-x-auto rounded-[14px] border border-[#2e2f34] bg-[#1d1e21] p-5"
              : "w-full overflow-x-auto"
          }
        >
          <table
            className={`min-w-full table-auto ${
              isUris
                ? "border-separate border-spacing-y-1.5 bg-transparent text-[#bdbbbc]"
                : isCustomCaseView
                  ? "border-collapse bg-white text-[#888888]"
                  : "border-collapse bg-white text-[#AAAAAA]"
            }`}
          >
            <thead
              className={`text-left uppercase ${
                isUris
                  ? "text-[11px] font-bold tracking-[0.08em] text-[#8a888b]"
                  : isCustomCaseView
                  ? "bg-white text-[11px] md:text-xs font-semibold tracking-wide text-[#707070]"
                  : "text-sm border-b-2 font-semibold"
              }`}
            >
              <tr>
                <th className={`px-4 ${isUris || isCustomCaseView ? "py-3 font-semibold" : "py-3"}`}>
                  Case ID
                </th>
                {hasSmileAccess && (
                  <th className={`px-4 ${isUris || isCustomCaseView ? "py-3 font-semibold" : "py-3"}`}>
                    Smile case
                  </th>
                )}
                <th className={`px-4 ${isUris || isCustomCaseView ? "py-3 font-semibold" : "py-3"}`}>
                  Upload Time
                </th>
                <th className={`px-4 ${isUris || isCustomCaseView ? "py-3 font-semibold" : "py-3"}`}>
                  Patient/Job
                </th>
                <th className={`px-4 ${isUris || isCustomCaseView ? "py-3 font-semibold" : "py-3"}`}>
                  Service Type
                </th>
                <th className={`px-4 ${isUris || isCustomCaseView ? "py-3 font-semibold" : "py-3"}`}>
                  Status
                </th>
                <th
                  className={`px-4 ${isUris || isCustomCaseView ? "py-3 font-semibold text-left" : "py-3 text-center"}`}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading || searching ? (
                <tr>
                  <td
                    colSpan={tableColSpan}
                    className="px-6 py-8 text-center text-gray-400"
                  >
                    Loading cases...
                  </td>
                </tr>
              ) : displayedCases.length > 0 ? (
                displayedCases.map((item, idx) => {
                  const statusLower = item.status?.toLowerCase() || "";
                  const isFilesPending = statusLower.includes("files pending");
                  const userCustomStyle = getUserCustomCaseStyle(item);
                  const defaultRowClass = idx % 2 ? "bg-[#F8F8F6]" : "bg-white";
                  const defaultBorderClass = idx % 2
                    ? "border-[#DDDDDD]"
                    : "border-[#E0E0E0]";
                  // Keep JU custom status row colours; otherwise soft peach zebra
                  const rowClass = isUris
                    ? "group"
                    : (userCustomStyle?.rowClass ??
                      (isCustomCaseView
                        ? idx % 2 === 0
                          ? "bg-[#F9F1EB]"
                          : "bg-white"
                        : defaultRowClass));
                  const statusBadgeBgClass =
                    userCustomStyle?.statusBadgeClass ??
                    (isCustomCaseView ? "bg-[#F9F1EB]" : defaultRowClass);
                  const statusBadgeBorderClass =
                    userCustomStyle?.borderClass ??
                    (isCustomCaseView ? "border-[#E8D9C4]" : defaultBorderClass);
                  const statusTextClass =
                    userCustomStyle?.statusTextClass ?? "text-[#555555]";
                  const cellPad = isUris
                    ? "h-[54px] border-y border-[#303136] bg-[#24252a] px-3 text-[13.5px] text-[#bdbbbc] group-hover:bg-[#292a2f]"
                    : isCustomCaseView
                      ? "px-6 py-4"
                      : "px-4 py-3";
                  const urisStatus = getUrisStatusStyle(item.status);

                  return (
                    <tr key={item.case_id} className={rowClass}>
                      <td
                        className={`${cellPad} break-words ${
                          isUris
                            ? "rounded-l-[10px] border-l border-[#303136] text-[13px] tabular-nums text-[#9C9A9B]"
                            : isCustomCaseView
                            ? "font-bold text-[#333333]"
                            : "rounded-l-lg"
                        }`}
                      >
                        {item.case_id}
                      </td>
                      {hasSmileAccess && (
                        <td className={`${cellPad} break-words`}>
                          {item.smile_id ? (
                            <a
                              href={smilePortalCaseHref(item.smile_id)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`font-semibold hover:underline break-all ${
                                isUris ? "text-[#F3A0A6]" : "text-[#1DA8C6]"
                              }`}
                            >
                              {item.smile_id}
                            </a>
                          ) : (
                            "—"
                          )}
                        </td>
                      )}
                      <td
                        className={`${cellPad} break-words max-w-xs ${
                          isUris
                            ? "tabular-nums text-[13px]"
                            : isCustomCaseView
                              ? "font-normal text-[#888888]"
                              : ""
                        }`}
                      >
                        {formatDate(item.creation_time_ms)}
                      </td>
                      <td
                        className={`${cellPad} break-words max-w-xs ${
                          isUris
                            ? "font-semibold text-white"
                            : isCustomCaseView
                            ? "font-bold text-[#333333]"
                            : "text-black"
                        }`}
                      >
                        {item.case_owner || "—"}
                      </td>
                      <td
                        className={`${cellPad} break-words max-w-xs ${
                          isUris
                            ? "text-[#a7a9b0]"
                            : isCustomCaseView
                              ? "font-normal text-[#888888]"
                              : ""
                        }`}
                      >
                        {(() => {
                          let services: string[] = [];

                          if (Array.isArray(item.service_type)) {
                            services = item.service_type;
                          } else if (typeof item.service_type === "string") {
                            services = item.service_type
                              .split(",")
                              .map((s: string) => s.trim());
                          }

                          return services.length > 0
                            ? services
                                .map(
                                  (s) =>
                                    Object.entries(serviceMap).find(
                                      ([, v]) => v === s
                                    )?.[0] || s
                                )
                                .join(", ")
                            : "—";
                        })()}
                      </td>
                      <td className={`${cellPad} break-words max-w-xs`}>
                        {isUris ? (
                          <span
                            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${urisStatus.pill}`}
                          >
                            <span
                              className={`h-1.5 w-1.5 rounded-full ${urisStatus.dot}`}
                            />
                            {item.status ?? "—"}
                          </span>
                        ) : isCustomCaseView ? (
                          <div
                            className={`inline-block px-3 py-1 rounded-full border font-semibold text-xs ${statusBadgeBgClass} ${statusBadgeBorderClass} ${statusTextClass}`}
                          >
                            {userCustomStyle?.statusLabel ?? item.status ?? "—"}
                          </div>
                        ) : (
                          <span
                            className={
                              isFilesPending ? "text-red-700 font-semibold" : ""
                            }
                          >
                            {item.status ?? "—"}
                          </span>
                        )}
                      </td>
                      <td
                        className={`${cellPad} ${
                          isUris
                            ? "rounded-r-[10px] border-r border-[#303136]"
                            : isCustomCaseView
                              ? ""
                              : "rounded-r-lg"
                        }`}
                      >
                        <div
                          className={`flex items-center gap-2.5 ${
                            isUris
                              ? "justify-end"
                              : isCustomCaseView
                                ? "justify-start"
                                : "justify-center"
                          }`}
                        >
                          <div className="relative">
                            <button
                              onClick={() => handleOpenModal(item.case_id)}
                              className={`${
                                isUris
                                  ? "inline-flex h-9 items-center rounded-lg border border-[#BF1E2E] bg-[#BF1E2E] px-3.5 text-[13px] font-bold text-white hover:bg-[#a51927]"
                                  : isCustomCaseView
                                  ? "text-xs px-3 py-1.5 rounded-full font-medium"
                                  : "text-[0.8vw] px-[1vw] py-[0.25vw] rounded-[1vw]"
                              } ${
                                isUris
                                  ? ""
                                  : isFilesPending
                                  ? "bg-red-600 hover:bg-red-700 text-white"
                                  : isVelvet || isCustomCaseView
                                    ? "bg-[#FCF2E5] hover:bg-[#F0E6D4] text-[#2C2C2C]"
                                    : "bg-[#29a3b4] hover:bg-[#208a99] text-white"
                              }`}
                            >
                              Details & Activity
                            </button>
                            {item?.pending_comments > 0 && (
                              <span
                                className={`absolute z-20 flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[11px] font-bold ${
                                  isUris
                                    ? "-right-2 -top-2 border-2 border-[#1d1e21] bg-[#E5A443] text-[#1a1b1e]"
                                    : "-right-2 -top-2 bg-red-500 text-white text-[0.65rem]"
                                }`}
                              >
                                {item.pending_comments}
                              </span>
                            )}
                          </div>

                          <button
                            type="button"
                            title={
                              isUris
                                ? "Add or update files (including late IOS scans for custom abutment)"
                                : "Upload files"
                            }
                            onClick={() =>
                              navigate(`/dashboard/upload/${item.case_id}`)
                            }
                            className={`transition ${
                              isUris
                                ? "inline-flex h-9 w-9 items-center justify-center rounded-lg border border-[#37383e] bg-[#141517] text-[#bdbbbc] hover:border-[#BF1E2E] hover:text-[#F06A74]"
                                : isFilesPending
                                ? "text-red-600 hover:text-red-700"
                                : isVelvet || isCustomCaseView
                                  ? "text-[#2C2C2C] hover:text-[#1a1a1a]"
                                  : "text-[#29a3b4] hover:text-[#1e8a96]"
                            }`}
                          >
                            <FiUpload size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={tableColSpan}
                    className="px-6 py-8 text-center text-gray-400"
                  >
                    No cases available.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <CaseDetailsModal
          isOpen={modalOpen}
          onClose={handleCloseModal}
          caseData={selectedCaseData}
        />
        {isJuClient && userId ? (
          <JuDailyCasesChart
            userId={userId}
            open={showCasesGraph}
            onClose={() => setShowCasesGraph(false)}
          />
        ) : null}
      </div>
    </>
  );
};

export default MyCase;
